import React from 'react';

// Static mock data — no auth needed
const MOCK = {
  assets:      { value: '$1,501,900', change: '+4.1%', amount: '+$59,104', period: '6M', dir: 'up' as const },
  liabilities: { value: '$577,500',   change: '↘ 1.3%', amount: '-$7,560',  period: '6M', dir: 'down' as const },
  debt:        { value: '38.5%',      change: 'Excellent', amount: '',        period: '',   dir: 'neutral' as const },
};

const green  = '#10b981';
const red    = '#f87171';
const purple = '#8b5cf6';
const bg     = '#0f1419';
const card   = '#1e293b';

const semanticColor = (dir: 'up' | 'down' | 'neutral', isLiability = false) => {
  if (dir === 'neutral') return purple;
  if (isLiability) return dir === 'down' ? green : red;
  return dir === 'up' ? green : red;
};

const ICONS: Record<string, string> = { Assets: '📈', Liabilities: '💳', 'Debt Ratio': '🎯' };
const MANAGE_PATHS: Record<string, string | null> = { Assets: '/assets', Liabilities: '/liabilities', 'Debt Ratio': null };

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: '4rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
      <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#475569' }}>{label}</span>
      <div style={{ flex: 1, height: '1px', background: '#1e293b' }} />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
      {children}
    </div>
  </div>
);

// ─── Shared props type ────────────────────────────────────────────────────────
type CardProps = { label: string; value: string; change: string; amount: string; period: string; dir: 'up'|'down'|'neutral'; isLiability?: boolean };

// ─── Style 1: Header-Led ─────────────────────────────────────────────────────
// Title + icon row at full readable weight. Number follows. Manage lives top-right.
const Style1: React.FC<CardProps> = ({ label, value, change, amount, period, dir, isLiability }) => {
  const color = semanticColor(dir, isLiability);
  const managePath = MANAGE_PATHS[label];
  return (
    <div style={{
      background: card,
      borderRadius: '16px',
      padding: '22px 24px 20px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
      display: 'flex',
      flexDirection: 'column',
      gap: '0',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>{ICONS[label]}</span>
          <span style={{ fontSize: '15px', fontWeight: 600, color: '#e2e8f0', letterSpacing: '-0.01em' }}>{label}</span>
        </div>
        {managePath && (
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#8b5cf6', cursor: 'pointer' }}>Manage →</span>
        )}
      </div>
      {/* Value */}
      <span style={{ fontSize: '1.95rem', fontWeight: 800, color: '#f8fafc', letterSpacing: '-0.03em', lineHeight: 1.05 }}>{value}</span>
      {/* Change row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color }}>{change}</span>
        {amount && <span style={{ fontSize: '12px', color: '#475569' }}>{amount}</span>}
        {period && <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>{period}</span>}
      </div>
    </div>
  );
};

// ─── Style 2: Ruled — 2px semantic top rule ──────────────────────────────────
// Semantic color rule at top. Title prominent below. Clean flat card.
const Style2: React.FC<CardProps> = ({ label, value, change, amount, period, dir, isLiability }) => {
  const color = semanticColor(dir, isLiability);
  const managePath = MANAGE_PATHS[label];
  return (
    <div style={{
      background: card,
      borderRadius: '12px',
      overflow: 'hidden',
      border: '1px solid rgba(255,255,255,0.06)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ height: '2px', background: color }} />
      <div style={{ padding: '20px 22px 18px', display: 'flex', flexDirection: 'column', gap: '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#cbd5e1', letterSpacing: '-0.01em' }}>{label}</span>
          {managePath && (
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#8b5cf6', cursor: 'pointer' }}>Manage →</span>
          )}
        </div>
        <span style={{ fontSize: '1.9rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.025em', lineHeight: 1.05 }}>{value}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color }}>{change}</span>
          {amount && <span style={{ fontSize: '12px', color: '#475569' }}>{amount}</span>}
          {period && <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>{period}</span>}
        </div>
      </div>
    </div>
  );
};

// ─── Style 3: Split band ─────────────────────────────────────────────────────
// Header band with full-weight title + Manage. Rule below. Value in body.
const Style3: React.FC<CardProps> = ({ label, value, change, amount, period, dir, isLiability }) => {
  const color = semanticColor(dir, isLiability);
  const managePath = MANAGE_PATHS[label];
  return (
    <div style={{
      background: card,
      borderRadius: '14px',
      border: '1px solid rgba(255,255,255,0.07)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.45)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        background: 'rgba(255,255,255,0.025)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>{ICONS[label]}</span>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>{label}</span>
        </div>
        {managePath && (
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#8b5cf6', cursor: 'pointer' }}>Manage →</span>
        )}
      </div>
      <div style={{ padding: '18px 20px 16px' }}>
        <div style={{ fontSize: '1.95rem', fontWeight: 800, color: '#f8fafc', letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color }}>{change}</span>
          {amount && <span style={{ fontSize: '12px', color: '#475569' }}>{amount}</span>}
          {period && <span style={{ fontSize: '11px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', padding: '1px 6px', color: '#64748b', fontWeight: 600 }}>{period}</span>}
        </div>
      </div>
    </div>
  );
};

// ─── Style 4: Tinted ─────────────────────────────────────────────────────────
// Semantic color bleeds into gradient background. Title at full weight.
const Style4: React.FC<CardProps> = ({ label, value, change, amount, period, dir, isLiability }) => {
  const color = semanticColor(dir, isLiability);
  const managePath = MANAGE_PATHS[label];
  const tint = color === green ? 'rgba(16,185,129,0.07)'
             : color === red   ? 'rgba(248,113,113,0.07)'
             :                   'rgba(139,92,246,0.07)';
  return (
    <div style={{
      background: `linear-gradient(145deg, ${card} 0%, ${tint} 100%)`,
      borderRadius: '14px',
      padding: '22px 24px 20px',
      border: `1px solid ${color}22`,
      boxShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px ${color}11`,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>{ICONS[label]}</span>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>{label}</span>
        </div>
        {managePath && (
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#8b5cf6', cursor: 'pointer' }}>Manage →</span>
        )}
      </div>
      <span style={{ fontSize: '1.9rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.025em', lineHeight: 1.05 }}>{value}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color }}>{change}</span>
        {amount && <span style={{ fontSize: '12px', color: '#475569' }}>{amount}</span>}
        {period && <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>{period}</span>}
      </div>
    </div>
  );
};

// ─── Style 5: Left Accent ────────────────────────────────────────────────────
// 3px semantic left bar. Title + Manage in a clear header row. No fuss otherwise.
const Style5: React.FC<CardProps> = ({ label, value, change, amount, period, dir, isLiability }) => {
  const color = semanticColor(dir, isLiability);
  const managePath = MANAGE_PATHS[label];
  return (
    <div style={{
      background: card,
      borderRadius: '12px',
      padding: '20px 22px 18px 20px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      borderLeft: `3px solid ${color}`,
      border: `1px solid rgba(255,255,255,0.06)`,
      borderLeftWidth: '3px',
      borderLeftColor: color,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>{label}</span>
        {managePath && (
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#8b5cf6', cursor: 'pointer' }}>Manage →</span>
        )}
      </div>
      <span style={{ fontSize: '1.9rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.025em', lineHeight: 1.05 }}>{value}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color }}>{change}</span>
        {amount && <span style={{ fontSize: '12px', color: '#475569' }}>{amount}</span>}
        {period && <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>{period}</span>}
      </div>
    </div>
  );
};

// ─── Style 6: Glass ──────────────────────────────────────────────────────────
// Frosted glass. Title at full weight with Manage. Inset top highlight.
const Style6: React.FC<CardProps> = ({ label, value, change, amount, period, dir, isLiability }) => {
  const color = semanticColor(dir, isLiability);
  const managePath = MANAGE_PATHS[label];
  return (
    <div style={{
      background: 'rgba(255,255,255,0.04)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderRadius: '16px',
      padding: '22px 24px 20px',
      border: '1px solid rgba(255,255,255,0.09)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.07)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>{ICONS[label]}</span>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>{label}</span>
        </div>
        {managePath && (
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#8b5cf6', cursor: 'pointer' }}>Manage →</span>
        )}
      </div>
      <span style={{ fontSize: '1.9rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.025em', lineHeight: 1.05 }}>{value}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color }}>{change}</span>
        {amount && <span style={{ fontSize: '12px', color: '#475569' }}>{amount}</span>}
        {period && <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>{period}</span>}
      </div>
    </div>
  );
};

// ─── Style 8: Split + Left Accent ────────────────────────────────────────────
// Style 3 (header band + rule) with a 3px semantic left bar.
// Bar color: always red for liabilities (type signal), semantic for assets/ratio.
// Period pill: colored to match the change direction.
const pillTint = (c: string) =>
  c === green  ? 'rgba(16,185,129,0.15)' :
  c === red    ? 'rgba(248,113,113,0.15)' :
                 'rgba(139,92,246,0.15)';

const Style8: React.FC<CardProps> = ({ label, value, change, amount, period, dir, isLiability }) => {
  const color    = semanticColor(dir, isLiability);           // change row + pill
  const barColor = isLiability ? red : semanticColor(dir, false); // left bar: type-based
  const managePath = MANAGE_PATHS[label];
  return (
    <div style={{
      position: 'relative',
      background: card,
      borderRadius: '14px',
      border: '1px solid rgba(255,255,255,0.07)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.45)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Left accent bar — absolutely positioned, full height */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: barColor }} />
      {/* Header band */}
      <div style={{
        padding: '14px 20px 14px 22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        background: 'rgba(255,255,255,0.025)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>{ICONS[label]}</span>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>{label}</span>
        </div>
        {managePath && (
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#8b5cf6', cursor: 'pointer' }}>Manage →</span>
        )}
      </div>
      {/* Value body */}
      <div style={{ padding: '18px 20px 16px 22px' }}>
        <div style={{ fontSize: '1.95rem', fontWeight: 800, color: '#f8fafc', letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color }}>{change}</span>
          {amount && <span style={{ fontSize: '12px', color: '#475569' }}>{amount}</span>}
          {period && (
            <span style={{
              fontSize: '11px',
              fontWeight: 600,
              color,
              background: pillTint(color),
              borderRadius: '4px',
              padding: '2px 7px',
            }}>{period}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Style 7: Assets-page Insight Card (exact match) ─────────────────────────
// Icon in purple/indigo gradient badge. Title at 1rem/600. Value at 1.5rem/700.
// Description line for context. Purple-tint border. This is the existing style
// used on the Assets, Liabilities, and Dashboard Financial Health sections.
const Style7: React.FC<CardProps> = ({ label, value, change, amount, period }) => {
  const description = [change, amount, period].filter(Boolean).join(' ');
  return (
    <div style={{
      background: card,
      borderRadius: '16px',
      padding: '24px',
      border: '1px solid rgba(139,92,246,0.2)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header: icon badge + title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
          borderRadius: '12px',
          fontSize: '20px',
          flexShrink: 0,
        }}>
          {ICONS[label]}
        </div>
        <span style={{ fontSize: '16px', fontWeight: 600, color: '#f8fafc' }}>{label}</span>
      </div>
      {/* Value */}
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f8fafc', marginBottom: '8px', letterSpacing: '-0.02em' }}>{value}</div>
      {/* Description */}
      <div style={{ fontSize: '0.875rem', color: '#cbd5e1', lineHeight: 1.4 }}>{description || 'No change data'}</div>
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────
const CardStylesDemo: React.FC = () => {
  const cards: CardProps[] = [
    { label: 'Assets',      ...MOCK.assets,      isLiability: false },
    { label: 'Liabilities', ...MOCK.liabilities, isLiability: true  },
    { label: 'Debt Ratio',  ...MOCK.debt,         isLiability: false },
  ];

  return (
    <div style={{ background: bg, minHeight: '100vh', padding: '48px 40px', fontFamily: 'inherit' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h1 style={{ color: '#f8fafc', fontSize: '1.25rem', fontWeight: 600, marginBottom: '8px' }}>KPI Card Styles</h1>
        <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '48px' }}>6 options — same data, different treatment. Navigate to /card-styles to view.</p>

        <Section label="1 — Header-Led · prominent title, no border, shadow only">
          {cards.map(c => <Style1 key={c.label} {...c} />)}
        </Section>

        <Section label="2 — Ruled · 2px semantic top rule, flat card">
          {cards.map(c => <Style2 key={c.label} {...c} />)}
        </Section>

        <Section label="3 — Split · header band separated by rule">
          {cards.map(c => <Style3 key={c.label} {...c} />)}
        </Section>

        <Section label="4 — Tinted · faint semantic color in background">
          {cards.map(c => <Style4 key={c.label} {...c} />)}
        </Section>

        <Section label="5 — Left Accent · semantic 3px bar">
          {cards.map(c => <Style5 key={c.label} {...c} />)}
        </Section>

        <Section label="6 — Glass · frosted, inset highlight">
          {cards.map(c => <Style6 key={c.label} {...c} />)}
        </Section>

        <Section label="8 — Split + Left Accent · header band rule AND semantic 3px left bar">
          {cards.map(c => <Style8 key={c.label} {...c} />)}
        </Section>

        <Section label="7 — Insight Card · existing style from Assets / Financial Health pages">
          {cards.map(c => <Style7 key={c.label} {...c} />)}
        </Section>
      </div>
    </div>
  );
};

export default CardStylesDemo;
